import React, { useState, useEffect } from 'react';
import api from '../api/client';
import SelectBox from './SelectBox';
import './PopupWMProduction.css';

const PopupSetsProduction = ({ isOpen, onClose, onSaved }) => {
    const [emplID, setEmplID] = useState('');
    const [toolsBrandID, setToolsBrandID] = useState('');
    const [scanneddata, setScanneddata] = useState('');
    const [toolsWarn, setToolsWarn] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [saving, setSaving] = useState(false);
    const [boxOf, setBoxOf] = useState({}); // sku → box size (product_details.box_size)

    useEffect(() => {
        if (!isOpen) return;
        setEmplID(''); setToolsBrandID(''); setScanneddata('');
        setToolsWarn(false); setErrorMessage(''); setSaving(false);
        api.get('/api/product-details')
            .then(r => setBoxOf(Object.fromEntries((r.data || []).map(d => [d.product_sku, d.box_size || '']))))
            .catch(() => {});
    }, [isOpen]);

    if (!isOpen) return null;

    const boxCount = scanneddata.split('\n').filter(l => l.trim()).length;
    // Scanned line = "<scanner>-<set name>, <SKU>, BOX#n/qty date time" (same split as /api/mbproduction):
    // one row per set with its box size, so a wrong box is visible right after the scan
    const scanned = [];
    for (const line of scanneddata.split('\n')) {
        const parts = line.split(',').map(s => s.trim());
        if (parts.length < 3) continue;
        const sku = parts[parts.length - 2].replace(/_U$/, '');
        const name = parts.slice(0, -2).join(', ').split('-').slice(1).join('-').trim();
        const row = scanned.find(s => s.sku === sku);
        if (row) row.boxes += 1; else scanned.push({ sku, name, boxes: 1 });
    }

    const handleScannerFocus = () => {
        if (!toolsBrandID) setToolsWarn(true);
    };

    const handleSave = async () => {
        if (!emplID) { setErrorMessage('Employee is required'); return; }
        if (!toolsBrandID) {
            setToolsWarn(true);
            setErrorMessage("Select which brand's tools were used");
            return;
        }
        if (!scanneddata.trim()) { setErrorMessage('Scan at least one box'); return; }
        setErrorMessage(''); setSaving(true);
        try {
            await api.post('/api/mbproduction', { emplID, toolsBrandID, scanneddata });
            onSaved?.();
            onClose();
        } catch (err) {
            setErrorMessage(err.response?.data?.error || 'Failed to save production record');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="popup open">
            <div className="popup__body">
                <div className="popup__content popup__content--wide sets-popup" style={{ maxWidth: 520, width: '100%', borderRadius: 8 }}>
                    <button className="popup__close-button" onClick={onClose}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <use xlinkHref="#closeBtn" />
                        </svg>
                    </button>

                    <h2 className="popup__title">Sets Production</h2>
                    <h3 className="popup__subtitle">Scan produced boxes with sets</h3>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 16 }}>
                        <div className="prod-header-field">
                            <label className="label">Employee</label>
                            <SelectBox
                                tableName="employees"
                                displayField="fullName"
                                updateField="employeeID"
                                selectedValue={emplID}
                                onChange={setEmplID}
                            />
                        </div>

                        <div className="prod-header-field">
                            <label className="label">Tools Brand</label>
                            <span className="sets-popup__hint">
                                Which brand's mini-tools were packed into these sets
                            </span>
                            <SelectBox
                                tableName="brands"
                                displayField="brandName"
                                updateField="brandID"
                                selectedValue={toolsBrandID}
                                onChange={(v) => { setToolsBrandID(v); setToolsWarn(false); }}
                            />
                        </div>

                        {toolsWarn && !toolsBrandID && (
                            <div className="prod-weight-warning">
                                <i className="fa fa-exclamation-triangle" />
                                Before scanning boxes, select which brand's tools were used —
                                they are written off from that brand's inventory.
                            </div>
                        )}

                        <div className="prod-header-field">
                            <label className="label">Scanned boxes</label>
                            <textarea
                                className="scannerform__textarea"
                                rows="6"
                                placeholder={toolsBrandID
                                    ? 'Scan boxes here (one per line)'
                                    : 'Select tools brand first'}
                                value={scanneddata}
                                readOnly={!toolsBrandID}
                                onFocus={handleScannerFocus}
                                onClick={handleScannerFocus}
                                onChange={e => setScanneddata(e.target.value)}
                            />
                            {boxCount > 0 && (
                                <span style={{ fontSize: 12, color: '#5B965D', fontWeight: 600 }}>
                                    {boxCount} box{boxCount !== 1 ? 'es' : ''} scanned
                                </span>
                            )}
                            {scanned.length > 0 && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
                                    {scanned.map(s => (
                                        <div key={s.sku} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                                            <span className="gtin-box-size" title="Box size the set is packed into, inches">
                                                <i className="fa-solid fa-box"></i>{boxOf[s.sku] || 'no box size'}
                                            </span>
                                            <span>{s.name || s.sku}</span>
                                            <span style={{ color: '#64748b' }}>× {s.boxes}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {errorMessage && <div className="field-error">{errorMessage}</div>}

                        <button className="btn btn__green" onClick={handleSave} disabled={saving}>
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PopupSetsProduction;
